import { ReactNode, memo, MouseEvent } from 'react';
import { Box } from '@mui/system';
import { MenuItem, Tooltip } from '@mui/material';

type InsertItemLayoutProps = {
  icon: ReactNode;
  title: string;
  description: string;
  id?: string; // id + data-testid
  shortcut?: string;
  action?: (event: MouseEvent<HTMLDivElement | HTMLLIElement>) => void; // onClick action
  disabled?: boolean; // disable menu item
};

export const InsertItemLayout = memo(
  ({
    icon,
    title,
    description,
    shortcut,
    action,
    id,
    disabled,
  }: InsertItemLayoutProps) => {
    return (
      <MenuItem
        id={id}
        data-testid={id}
        onClick={action}
        disabled={disabled}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr', // Two columns for title and shortcut
            gridTemplateRows: 'auto', // One row for the whole content
            alignItems: 'top', // Align items along the baseline
            gap: '0.5rem',
            maxWidth: '250px',
          }}
        >
          {icon}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr', // Two columns for title and shortcut
              gridTemplateRows: 'auto', // One row for the whole content
              alignItems: 'baseline', // Align items along the baseline
              gap: '0.25rem', // Add a gap between the two columns

              textWrap: 'wrap',
            }}
          >
            <Tooltip
              title={description}
              placement="left"
              disableInteractive
            >
              <Box sx={{ fontSize: '1rem' }}>{title}</Box>
            </Tooltip>
            <Box
              sx={{
                fontSize: '0.75rem',
                fontWeight: '300',
                textTransform: 'lowercase',
              }}
            >
              {shortcut}
            </Box>
            <Box
              sx={{
                fontSize: '0.75rem',
                fontWeight: '300',
                gridColumn: '1 / span 2',
                overflow: 'hidden',
                textOverflow: {
                  xs: 'initial',
                  md: 'ellipsis',
                },
                whiteSpace: {
                  xs: 'initial',
                  md: 'nowrap',
                },
              }}
            >
              {description}
            </Box>
          </Box>
        </Box>
      </MenuItem>
    );
  },
);

InsertItemLayout.displayName = 'InsertItemLayout';
